import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Params, Router } from '@angular/router';
import { TenantDto } from '@nusign/picflow-backend-types';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, filter, take } from 'rxjs';
import { generateUuid } from '../lib/generate-uuid';
import { getAllRouteData } from '../lib/get-all-route-data';
import { isIframe } from '../lib/is-iframe';
import { loadScript } from '../lib/load-script';
import { PicflowAnalytics } from '../picflow-analytics';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { GrowthBookExperimentService } from './growth-book-experiment.service';
import { MetaService } from './meta.service';
import { StateService } from './state.service';
import { StorageService } from './storage.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    analytics: any;
    dataLayer: { [key: string]: unknown }[];
    Intercom: (action: string, ...args: unknown[]) => void;
    intercomSettings: { [key: string]: unknown };
    attachEvent: (event: string, callback: () => void) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements PicflowAnalytics {
  private router = inject(Router);
  private config = inject(AppConfigService);
  private state = inject(StateService);
  private metaService = inject(MetaService);
  private route = inject(ActivatedRoute);
  private cookies = inject(CookieService);
  private storage = inject(StorageService);
  private browserService = inject(BrowserService);
  private zone = inject(NgZone);
  private growthbookExperimentService = inject(GrowthBookExperimentService);
  private platformId = inject<string>(PLATFORM_ID);
  private initted = false;
  private lastValues = {};
  private isIframe: boolean;
  private currentUrl: string;
  public userTraits: { [key: string]: number | string } = {}; // expected to be set by the loggedin analytics service
  public lastUrl = '';
  public clerkLoadTime: number;
  private lastButtonClicked: {
    ctaName: string;
    ctaText: string;
    ctaIcon: string;
    ctaLocation: string;
    clickTime: number;
  };

  private growthbookExperiments$ = this.growthbookExperimentService.onExperimentViewed
    .pipe(filter(Boolean))
    .subscribe(data => {
      if (this.growthbookExperiments.some(e => e.name === data.name)) return;
      this.growthbookExperiments.push(data);
      this.track('experiment_viewed', false, {
        experiment_name: data.name,
        feature_id: data.featureId,
        result_value: data.resultValue,
      });
    });

  private growthbookExperiments: {
    name: string;
    featureId: string;
    resultValue: string;
  }[] = [];

  private firstInteraction$ = new BehaviorSubject(false);

  event$ = new BehaviorSubject<{ event: string; properties: { [key: string]: unknown } }>(undefined);

  tenant: TenantDto;

  constructor() {
    if (isPlatformServer(this.platformId)) return;
    this.setupFirstInteractionLogic();
    this.isIframe = isIframe();

    this.zone.runOutsideAngular(() => {
      this.cioInit();
      this.tagmanagerInit();
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          take(1)
        )
        .subscribe(() => this.init());
    });

    this.router.events.subscribe(async event => {
      if (this.state.impersonating) return;
      if (event instanceof NavigationStart) {
        this.extractAndSaveUtmParams(event.url);
        this.extractAndSaveTrackingParams(event.url);
        this.state.nextPageIsNotFound = false;
      } else if (event instanceof NavigationEnd) {
        if (this.lastUrl.split('?')[0].split('#')[0] === event.urlAfterRedirects.split('?')[0].split('#')[0]) return;

        this.lastUrl = event.urlAfterRedirects.split('email=')[0];
        const path = event.urlAfterRedirects.split('email=')[0];
        const title = this.metaService.getTitle();
        console.debug('PAGEVIEW:', title);

        this.currentUrl = location.origin + path;

        this.extractAndSaveFirstUrl(path);

        const data = getAllRouteData(this.route.snapshot);
        let gallery_id: string;
        let image_id: string;
        let tenant_id: string;
        if (data.gallery) {
          gallery_id = data.gallery.id;
          if (data.gallery.tenant) tenant_id = data.gallery.tenant?.id || data.gallery.tenant;
        }
        if (data.image) image_id = data.image.id;
        if (data.data?.image) image_id = data.data.image.id;

        window.Intercom?.('boot');
        if (!this.config.isTenantDomain) {
          this.pushToDataLayer(this.mapTagmanagerData(this.userTraits), true);
        }
        this.pushToDataLayer({
          event: 'picflow_page_view',
          page_location: location.origin + path,
          page_title: title,
          gallery_id,
          image_id,
          tenant_id,
        });

        this.cioPage(title, path);
      }
    });
  }

  private init() {
    if (this.initted) return;
    this.initted = true;
    this.cioLoad();
    this.tagmanagerLoad();
    this.cloudflareAnalyticsLoad();
  }

  public setTenant(tenant: TenantDto): void {
    this.tenant = tenant;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapTagmanagerData(_data: { [key: string]: any }) {
    const data = Object.assign({}, _data);

    // remove tracking parameters
    delete data.li_fat_id;
    delete data.epik;
    delete data.gclid;
    delete data.gbraid;
    delete data.wbraid;
    delete data.ttclid;
    delete data.msclkid;
    delete data.twclid;
    delete data.fbclid;
    delete data.fbc;
    delete data.fbp;
    delete data.rdt_cid;

    // clarify button fields
    data.button_name = data.name;
    data.button_location = data.location;
    delete data.name;
    delete data.location;

    return data;
  }

  private extraMixpanelProperties() {
    return {
      devicePixelRatio: window.devicePixelRatio,
      resolution: `${window.screen.width}x${window.screen.height}`,
      tenantId: this.state.visitingTenant?.id ?? this.tenant?.id ?? this.state.sharedWithMeActiveTenant$.value?.id,
    };
  }

  private getAbTestTraits() {
    const abTests: { [key: string]: string } = {};
    this.growthbookExperiments.forEach(e => (abTests[e.name] = e.resultValue || '__control__'));
    return { abTests };
  }

  public setLastButtonClicked(ctaName: string, ctaText: string, ctaIcon: string, ctaLocation: string) {
    this.lastButtonClicked = { ctaName, ctaText, ctaIcon, ctaLocation, clickTime: Date.now() };
  }
  public removeLastButtonClicked() {
    this.lastButtonClicked = undefined;
  }

  public track(
    event: string,
    isImportant = true,
    extraProperties: {
      [key: string]:
        | string
        | number
        | boolean
        | { [key: string]: string | number | boolean }
        | { [key: string]: string | number | boolean }[];
    } = {}
  ) {
    if (this.state.impersonating) return;
    if (!event || typeof event !== 'string') {
      console.warn('Track event is not a string', event);
      return;
    }
    console.debug('EVENT:', event, extraProperties);
    if (!isPlatformBrowser(this.platformId)) return;

    if (isImportant) {
      window.Intercom?.('trackEvent', event);
    }
    const order_id = (extraProperties.order_id || `picflow_${generateUuid()}`) as string;

    const properties = {
      ...this.userTraits,
      ...this.extraMixpanelProperties(),
      ...extraProperties,
      ...this.getAbTestTraits(),
      order_id,
      current_url: this.currentUrl,
    };
    this.cioTrack(event, properties);
    if (!this.config.isTenantDomain) {
      this.pushToDataLayer(this.mapTagmanagerData(properties), true);
      this.pushToDataLayer({ event });
    }

    if (event === 'checklist item done' && extraProperties.name === 'uploadAssets') {
      this.track('First Upload', false, extraProperties);
      this.track('onboarding step done: first upload', false, extraProperties);
    }

    this.event$.next({ event, properties });
  }

  public getCioContext() {
    return {
      connection: this.browserService?.isTenantDomain() ? 'galleries-and-profiles' : 'web-and-app',
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async cioTrack(event: string, params: { [key: string]: any }) {
    try {
      window.analytics?.track(event, params, {
        context: this.getCioContext(),
      });
    } catch {
      console.warn('Unable to send segment track');
    }
  }

  private async cioPage(title: string, path: string) {
    const isAssetPage = path && this.browserService.urlIsAssetUrl(path);
    if (isAssetPage) return;
    try {
      const url = window.location.origin + path;
      window.analytics?.page(
        title,
        {
          name: title,
          title,
          url,
          path,
          notFound: this.state.nextPageIsNotFound,
          clerkLoadTime: this.clerkLoadTime,
          ...this.userTraits,
          ...this.extraMixpanelProperties(),
          ...this.getAbTestTraits(),
          ...this.lastButtonClickedParams(),
        },
        {
          context: this.getCioContext(),
        }
      );
      this.clerkLoadTime = undefined; // reset after sending
    } catch (err) {
      console.warn('Unable to send segment page', err);
    }
  }

  public lastButtonClickedParams() {
    if (!this.lastButtonClicked) return {};
    return {
      ...this.lastButtonClicked,
      duration: Date.now() - this.lastButtonClicked.clickTime,
    };
  }

  public reportKeyboardShortcut(name: string, location: string, extra: { [key: string]: string | number } = {}) {
    this.track('keyboard_shortcut', false, { name, location, ...extra });
  }

  public showIntercom() {
    this.intercomInit();
    this.track('open_chat');
    window.Intercom?.('show');
  }

  private intercomInitted = false;
  public intercomInit() {
    if (this.intercomInitted) return;
    this.intercomInitted = true;
    if (window.Intercom) return console.debug('Not loading intercom was already loaded');
    if (this.config.env === 'local' && this.config.origin !== 'http://127.0.0.1:4200') {
      return console.debug('Not loading intercom on local as it doesnt work anyway');
    }
    if (this.isIframe) return;

    if (!window.intercomSettings) {
      window.intercomSettings = {
        app_id: this.config.intercomAppId,
        hide_default_launcher: true,
      };
    }

    const i = function () {
      // eslint-disable-next-line prefer-rest-params
      i['c'](arguments);
    };
    i['q'] = [];
    i['c'] = function (args) {
      i['q'].push(args);
    };
    window.Intercom = i;

    loadScript('https://widget.intercom.io/widget/' + this.config.intercomAppId, { ignoreErrors: true });
  }

  private async tagmanagerInit() {
    window.dataLayer = [];

    function gtag() {
      // eslint-disable-next-line prefer-rest-params , @typescript-eslint/no-explicit-any
      (window.dataLayer as any).push(arguments);
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (gtag as any)('consent', 'default', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    });

    window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  }

  private cloudflareAnalyticsLoad() {
    if (location.search.includes('noBeacon')) return;
    if (this.browserService.getTenantCustomDomain()) return;
    if (this.isIframe) return;
    if (this.config.env === 'local') return;
    loadScript('https://static.cloudflareinsights.com/beacon.min.js', {
      ignoreErrors: true,
      attributes: {
        'data-cf-beacon': `{"token": "e66e38c9e77944d3aac7a496d60a2041"}`,
      },
    });
  }

  private async tagmanagerLoad() {
    if (this.config.env !== 'production') return;
    if (this.browserService.isTenantDomain()) return; // Don't load tagmanager on tenant domains
    if (this.isIframe) return;
    if (location.search.includes('noTagManager')) return;

    if (this.tenant?.id_stripe_customer) {
      window.dataLayer.push({ event: 'start_pw', pw_user_id: this.tenant.id_stripe_customer });
    } else {
      window.dataLayer.push({ event: 'start_pw', pw_user_email: this.state.user?.email });
    }

    this.firstInteraction$.pipe(filter(Boolean), take(1)).subscribe(() => {
      loadScript(
        'https://measure.picflow.com/gtm.js?id=' + this.config.gtmWorkspaceId + this.config.gtmEnvironmentExtra
      );
    });
  }

  hasSetFirstUrl = false;
  private setFirstUrlFromClientDomain(url: string) {
    if (this.hasSetFirstUrl) return;
    this.hasSetFirstUrl = true;
    if (!this.browserService.isTenantDomain()) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${this.config.correctPicflowUrl}/assets/set-first-path.html#${url}`;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);
  }

  private extractAndSaveFirstUrl(url: string) {
    if (url.includes('/auth/authorize')) return;
    if (!this.storage.get('firstUrl')) {
      this.storage.set('firstUrl', { time: Date.now(), url: location.origin + url });
    }
    this.setFirstUrlFromClientDomain(location.origin + url);
  }

  private extractAndSaveUtmParams(url: string) {
    if (!this.storage.has('firstReferrer') && document.referrer) {
      this.storage.set('firstReferrer', { time: Date.now(), referrer: document.referrer });
    }

    const searchString = url.split('?')[1];
    if (!searchString || !searchString.includes('utm_')) return;
    const utmSearchTerms: { key: string; value: string | number }[] = searchString
      .split('&')
      .filter(s => s.startsWith('utm_'))
      .map(s => ({ key: s.split('=')[0].split('utm_')[1], value: decodeURIComponent(s.split('=')[1]) }))
      .filter(s => ['source', 'medium', 'campaign', 'id', 'term', 'content'].includes(s.key));

    if (!utmSearchTerms.length) return;

    utmSearchTerms.push({ key: 'time', value: Date.now() });

    this.storage.set('lastUtm', utmSearchTerms);
    if (!this.storage.has('firstUtm')) this.storage.set('firstUtm', utmSearchTerms);
  }

  private getQueryParam(key: string, url: string) {
    const searchString = url.split('?')[1]?.split('#')[0];
    return searchString
      ?.split('&')
      .find(s => s.startsWith(key))
      ?.split('=')[1];
  }

  private extractAndSaveTrackingParams(url: string) {
    const trackingParams = {
      li_fat_id: this.getQueryParam('li_fat_id', url), // linkedin
      epik: this.getQueryParam('epik', url), // linkedin
      gclid: this.getQueryParam('gclid', url), // google
      gbraid: this.getQueryParam('gbraid', url), // google
      wbraid: this.getQueryParam('wbraid', url), // google
      ttclid: this.getQueryParam('ttclid', url), // tiktok
      msclkid: this.getQueryParam('msclkid', url), // bing
      twclid: this.getQueryParam('twclid', url), // twitter
      fbclid: this.getQueryParam('fbclid', url), // facebook
      fbc: this.cookies.get('_fbc'), // facebook
      fbp: this.cookies.get('_fbp') || undefined, // facebook
      rdt_cid: this.getQueryParam('rdt_cid', url), // reddit
    };
    if (Object.values(trackingParams).some(Boolean)) {
      const params = this.getTrackingParams();
      for (const key in trackingParams) {
        if (trackingParams[key]) params[key] = trackingParams[key];
      }
      this.storage.set('trackingParams', params);
    }
  }

  public getTrackingParams() {
    // googleClickParams is for backwards compatibility
    const params = this.storage.get<Params>('trackingParams') || this.storage.get<Params>('googleClickParams') || {};

    // Only one of these can be set at a time
    if (params.gclid) {
      delete params.gbraid;
      delete params.wbraid;
    } else if (params.gbraid) {
      delete params.gclid;
      delete params.wbraid;
    } else if (params.wbraid) {
      delete params.gclid;
      delete params.gbraid;
    }

    return params;
  }

  public getFirstUrl(): string {
    return this.storage.get<{ time: number; url: string }>('firstUrl')?.url;
  }

  private cioInit() {
    const i = 'analytics',
      analytics = (window[i] = window[i] || []);
    if (!analytics.initialize)
      if (analytics.invoked) {
        console.error('Snippet included twice.');
      } else {
        analytics.invoked = !0;
        analytics.methods = [
          'trackSubmit',
          'trackClick',
          'trackLink',
          'trackForm',
          'pageview',
          'identify',
          'reset',
          'group',
          'track',
          'ready',
          'alias',
          'debug',
          'page',
          'once',
          'off',
          'on',
          'addSourceMiddleware',
          'addIntegrationMiddleware',
          'setAnonymousId',
          'addDestinationMiddleware',
        ];
        analytics.factory = function (e) {
          return function () {
            // eslint-disable-next-line prefer-rest-params
            const t = Array.prototype.slice.call(arguments);
            t.unshift(e);
            analytics.push(t);
            return analytics;
          };
        };
        for (let e = 0; e < analytics.methods.length; e++) {
          const key = analytics.methods[e];
          analytics[key] = analytics.factory(key);
        }

        analytics.load = (key, e) => {
          loadScript('https://cdp.picflow.com/v1/analytics-js/snippet/' + key + '/analytics.min.js', {
            ignoreErrors: true,
          });
          analytics._writeKey = key;
          analytics._loadOptions = e;
          analytics._cdn = 'https://cdp.picflow.com';
        };
        analytics.SNIPPET_VERSION = '4.15.3';
      }
  }
  private async cioLoad() {
    if (location.search.includes('noCio')) return;
    window.analytics.load(this.config.cioKey, {
      integrations: {
        'Customer.io Data Pipelines': {
          apiHost: 'cdp.picflow.com/v1',
        },
      },
    });
  }

  private pushToDataLayer(obj: { [key: string]: string | number }, ignoreDuplicates = false) {
    if (ignoreDuplicates) {
      const sameKeys = Object.keys(obj).filter(key => this.lastValues[key] === obj[key]);
      sameKeys.forEach(key => delete obj[key]);
      if (Object.keys(obj).length === 0) return;
      this.lastValues = { ...this.lastValues, ...obj };
    }
    if (Object.keys(obj).length === 0) return;
    window.dataLayer.push(obj);
  }

  private setupFirstInteractionLogic() {
    const interactionClick = () => {
      this.firstInteraction$.next(true);
      window.removeEventListener('click', interactionClick, true);
      window.removeEventListener('scroll', interactionScroll, true);
    };

    const interactionScroll = () => {
      this.firstInteraction$.next(true);
      window.removeEventListener('click', interactionClick, true);
      window.removeEventListener('scroll', interactionScroll, true);
    };

    window.addEventListener('click', interactionClick, true);
    window.addEventListener('scroll', interactionScroll, true);
  }
}
